import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { Box, TextField } from "@mui/material";
import "./quill.css";
import { useFormik } from "formik";
import { MyNewsItemFormFilter } from "my-news/components";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcQuillToolbar,
} from "oc/components";
import "quill/dist/quill.snow.css";
import * as Yup from "yup";

const validationSchema = (t: (key: string) => string) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const schema = Yup.object({
    title: Yup.string().required(t("MY_NEWS_TITLE_IS_REQUIRED")),
    description: Yup.string().required(t("MY_NEWS_DESCRIPTION_IS_REQUIRED")),
    content: Yup.string()
      .test({
        name: "not-empty",
        message: t("MY_NEWS_CONTENT_IS_REQUIRED"),
        test: function (value) {
          return !!value && value !== "<p><br></p>";
        },
      })
      .required(t("MY_NEWS_CONTENT_IS_REQUIRED")),
    validFrom: Yup.date()
      .nullable()
      .test({
        name: "validFrom",
        exclusive: false,
        params: {},
        test: function (value) {
          const published = this.parent.publish;
          const isEdit = this.parent.isEdit;

          if (published && value && value < today && isEdit) {
            return true;
          }

          if (value && value < today && !published && !isEdit) {
            return this.createError({
              path: "validFrom",
              message: t("MY_NEWS_DATE_CANNOT_BE_EARLIER"),
            });
          }

          if (!value && this.parent.publish) {
            return this.createError({
              message: t("OC_START_DATE_REQUIRED"),
              path: "validFrom",
            });
          }
          if (value && this.parent.publish) {
            if (value < today) {
              return this.createError({
                message: t("MY_NEWS_DATE_CANNOT_BE_EARLIER"),
                path: "validFrom",
              });
            }
          }
          return true;
        },
      }),
    validTo: Yup.date()
      .nullable()
      .test({
        name: "validTo",
        exclusive: false,
        params: {},
        message: t("OC_END_DATE_BEFORE_START_DATE"),
        test: function (value) {
          if (value) {
            return value >= this.parent.validFrom;
          }
          return true;
        },
      }),
  });
  return schema;
};

type MyNewsItemFormProps = {
  initialValues: {
    title: string;
    description: string;
    content: string;
    validFrom?: Date | null;
    validTo?: Date | null;
    publish?: boolean;
    isEdit?: boolean;
    [key: string]: any;
  };
  handleSubmit: (values: any) => void;
  contract?: any;
  roleFilters?: any;
  handleAddFilter?: (...args: any[]) => void;
  handleRemoveFilter?: (...args: any[]) => void;
  resetRoleFilters?: (...args: any[]) => void;
  cardLabel?: string;
  loading?: boolean;
  error?: any;
  enableReinitialize?: boolean;
  initialRoleFilter?: any;
  publishedAt?: any;
};

export const MyNewsItemForm: React.FC<MyNewsItemFormProps> = ({
  initialValues,
  handleSubmit,
  contract,
  roleFilters,
  handleAddFilter,
  handleRemoveFilter,
  resetRoleFilters,
  cardLabel,
  loading,
  error,
  enableReinitialize = false,
  initialRoleFilter,
  publishedAt,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const formik = useFormik({
    enableReinitialize: enableReinitialize,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: initialValues,
    validationSchema: validationSchema(t),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleCancel = () => {
    history.goBack();
  };

  const handleSave = async () => {
    await formik.setFieldValue("publish", false);
    formik.handleSubmit();
  };

  const handleSaveAndPublish = async () => {
    await formik.setFieldValue("publish", true);
    formik.handleSubmit();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  const handleConfirmationCancel = () => {
    if (buttonDisabled) {
      return handleCancel;
    }
    return () => setShowConfirmDialog(true);
  };

  const contentError = formik.touched.content && Boolean(formik.errors.content);
  const contentErrorText = formik.touched.content && formik.errors.content;

  const buttonDisabled = _.isEqual(initialValues, formik.values);

  return (
    <Box>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleCancel}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_GO_BACK")}
        texts={[t("OC_CONFIRM_GO_BACK_TEXT")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <MyNewsItemFormFilter
        formik={formik}
        roleFilters={roleFilters}
        handleAddFilter={handleAddFilter ?? (() => { })}
        handleRemoveFilter={handleRemoveFilter ?? (() => { })}
        resetRoleFilters={resetRoleFilters ?? (() => { })}
        contract={contract}
        initialValues={initialValues}
        initialRoleFilter={initialRoleFilter}
        published={publishedAt}
      />
      <OcCard label={cardLabel} loading={loading} error={error}>
        <Box padding="16px 0px 0px 0px">
          <TextField
            id="title"
            label={t("MY_NEWS_TITLE")}
            name="title"
            variant="outlined"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.touched.title && Boolean(formik.errors.title)}
            onFocus={() => formik.setTouched({}, false)}
            fullWidth
            helperText={
              formik.touched.title && formik.errors.title ? (
                <span style={{ display: "block", textAlign: "left" }}>
                  {formik.errors.title}
                </span>
              ) : (
                <span style={{ display: "block", textAlign: "right" }}>
                  {`${formik.values.title.length}/255`}
                </span>
              )
            }
          />
          <Box height="32px" />
          <TextField
            id="description"
            label={t("MY_NEWS_DESCRIPTION")}
            name="description"
            variant="outlined"
            value={formik.values.description}
            onChange={formik.handleChange}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={
              (formik.touched.description && formik.errors.description) || " "
            }
            onFocus={() => formik.setTouched({}, false)}
            fullWidth
            minRows={3}
            multiline
            style={{ height: "auto" }}
          />
          <Box height="16px" />
          <Box
            style={{
              border: `1px solid ${contentError ? "#cc0033" : "#C6CACC"}`,
              borderRadius: "4px",
            }}
            onFocus={() => formik.setTouched({}, false)}
          >
            <OcQuillToolbar formik={formik} />
          </Box>
          <Box
            style={{
              color: "#cc0033",
              paddingLeft: "16px",
              fontSize: "12px",
              marginTop: "3px",
              letterSpacing: "0.4px",
              minHeight: "16px",
            }}
          >
            {contentErrorText}
          </Box>
          <OcCardButtonBar
            handleLeftButton={handleConfirmationCancel()}
            leftButtonColor="secondary"
            leftButtonText={t("OC_CANCEL")}
            handleRightButton={handleSave}
            rightButtonDisabled={buttonDisabled}
            rightButtonColor="secondary"
            rightButtonVariant="outlined"
            rightButtonText={!publishedAt ? t("OC_SAVE") : ""}
            handlePrimaryButton={handleSaveAndPublish}
            primaryButtonDisabled={buttonDisabled}
            primaryButtonText={t("MY_NEWS_SAVE_AND_PUBLISH")}
            primaryButtonId="save-button"
          />
        </Box>
      </OcCard>
    </Box>
  );
};

export default MyNewsItemForm;
